import { memo, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { 
  ArrowLeft, 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  Mail,
  ListTodo,
  CheckSquare,
  Target,
  Zap,
  AlertTriangle,
  TrendingUp
} from 'lucide-react'
import { useJiraPersonDetail } from './hooks'
import JiraTaskCard from './components/JiraTaskCard'
import JiraStatusBreakdown from './components/JiraStatusBreakdown'
import JiraProjectBreakdown from './components/JiraProjectBreakdown'
import JiraDependencies from './components/JiraDependencies'
import JiraPerformanceRadar from './components/JiraPerformanceRadar'
import { 
  calculatePersonPerformance, 
  createPersonRadarData,
  getPerformanceLevel,
  getPerformanceLevelConfig,
  type PerformanceMetrics,
  type RadarChartData
} from '@/lib/jira/metrics'

// ============================================================
// LOADING SKELETON
// ============================================================

const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    </div>
  )
})

// ============================================================
// PERFORMANCE SCORE CARD
// ============================================================

const PerformanceScoreCard = memo(function PerformanceScoreCard({
  metrics,
}: {
  metrics: PerformanceMetrics
}) {
  const level = getPerformanceLevel(metrics.overallScore)
  const levelConfig = getPerformanceLevelConfig(level)

  return (
    <Card variant="default" padding="md" className="border-l-4 border-l-primary-500">
      <CardHeader compact>
        <CardTitle compact className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Performans Özeti
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {/* Overall Score */}
          <div className="col-span-2 sm:col-span-1 text-center p-4 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-800 rounded-xl">
            <div className={`text-4xl font-bold ${levelConfig.color} mb-1`}>
              {metrics.overallScore}
            </div>
            <div className={`text-xs px-2 py-1 rounded-full inline-block ${levelConfig.bgColor} ${levelConfig.color}`}>
              {levelConfig.label}
            </div>
            <div className="text-xs text-gray-400 mt-1">Genel Puan</div>
          </div>

          {/* Completion Rate */}
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/10 rounded-xl">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                %{metrics.completionRate}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Tamamlama</div>
          </div>

          {/* Velocity */}
          <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-900/10 rounded-xl">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Zap className="h-4 w-4 text-indigo-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {metrics.velocityScore}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Haftalık</div>
          </div>

          {/* Blocked Ratio */}
          <div className={`text-center p-3 rounded-xl ${
            metrics.blockedRatio > 20 
              ? 'bg-red-50 dark:bg-red-900/10' 
              : 'bg-gray-50 dark:bg-gray-800/50'
          }`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              <AlertTriangle className={`h-4 w-4 ${
                metrics.blockedRatio > 20 ? 'text-red-500' : 'text-gray-400'
              }`} />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                %{metrics.blockedRatio}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Engelli</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Performans Seviyesi</span>
            <span>{metrics.overallScore}/100</span>
          </div>
          <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                metrics.overallScore >= 75 ? 'bg-green-500' :
                metrics.overallScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${metrics.overallScore}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
})

// ============================================================
// USER HEADER CARD
// ============================================================

const UserHeaderCard = memo(function UserHeaderCard({
  user,
  openTaskCount,
  completedTaskCount,
  metrics,
}: {
  user: {
    displayName: string
    emailAddress?: string
    avatarUrls?: { '48x48'?: string }
  }
  openTaskCount: number
  completedTaskCount: number
  metrics: PerformanceMetrics
}) {
  const avatarUrl = user.avatarUrls?.['48x48']
  const initials = user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  const level = getPerformanceLevel(metrics.overallScore)
  const levelConfig = getPerformanceLevelConfig(level)

  return (
    <Card 
      variant="default" 
      padding="lg" 
      className="bg-gradient-to-r from-primary-50 via-white to-indigo-50 dark:from-primary-900/20 dark:via-gray-800 dark:to-indigo-900/20"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={user.displayName}
              className="w-20 h-20 rounded-2xl ring-4 ring-white dark:ring-gray-700 shadow-xl"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-xl">
              {initials}
            </div>
          )}
          <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${levelConfig.bgColor} flex items-center justify-center text-sm`}>
            {levelConfig.icon}
          </div>
        </div>

        {/* User Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {user.displayName}
            </h1>
            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${levelConfig.bgColor} ${levelConfig.color}`}>
              {levelConfig.label}
            </span>
          </div>
          
          {user.emailAddress && (
            <a
              href={`mailto:${user.emailAddress}`}
              className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <Mail className="h-4 w-4" />
              {user.emailAddress}
            </a>
          )}

          {/* Quick Stats */}
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-gray-900 dark:text-white">{openTaskCount}</span>
              <span>açık görev</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="font-semibold text-gray-900 dark:text-white">{completedTaskCount}</span>
              <span>tamamlandı</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <TrendingUp className="h-4 w-4 text-primary-500" />
              <span className="font-semibold text-gray-900 dark:text-white">{metrics.overallScore}</span>
              <span>puan</span>
            </div>
          </div>
        </div>

        {/* Big Score */}
        <div className="hidden lg:block text-center p-6">
          <div className={`text-5xl font-bold ${levelConfig.color}`}>
            {metrics.overallScore}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Performans Puanı
          </div>
        </div>
      </div>
    </Card>
  )
})

// ============================================================
// TASKS LIST WITH TABS
// ============================================================

const TasksList = memo(function TasksList({
  openTasks,
  completedTasks,
}: {
  openTasks: any[]
  completedTasks: any[]
}) {
  const [activeTab, setActiveTab] = useState<'open' | 'completed'>('open')
  const [showAll, setShowAll] = useState(false)

  const displayTasks = activeTab === 'open' ? openTasks : completedTasks
  const visibleTasks = showAll ? displayTasks : displayTasks.slice(0, 5)

  return (
    <Card variant="default" padding="md">
      <CardHeader compact>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setActiveTab('open'); setShowAll(false) }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'open'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <ListTodo className="h-4 w-4" />
            Açık Görevler ({openTasks.length})
          </button>
          <button
            onClick={() => { setActiveTab('completed'); setShowAll(false) }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'completed'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <CheckSquare className="h-4 w-4" />
            Tamamlanan ({completedTasks.length})
          </button>
        </div>
      </CardHeader>
      <CardContent compact>
        {visibleTasks.length > 0 ? (
          <>
            <div className="space-y-3">
              {visibleTasks.map(task => (
                <JiraTaskCard key={task.id} task={task} />
              ))}
            </div>
            {displayTasks.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                className="w-full mt-4"
              >
                {showAll ? 'Daha az göster' : `Tümünü göster (${displayTasks.length})`}
              </Button>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            {activeTab === 'open' ? (
              <>
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Harika! Açık görev yok.</p>
              </>
            ) : (
              <>
                <ListTodo className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Henüz tamamlanan görev yok.</p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
})

// ============================================================
// MAIN COMPONENT
// ============================================================

const JiraPersonDetailEnhanced = memo(function JiraPersonDetailEnhanced() {
  const { accountId } = useParams<{ accountId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error, refresh } = useJiraPersonDetail(accountId || null)

  // Hesaplanmış metrikler
  const performanceMetrics = useMemo<PerformanceMetrics | null>(() => {
    if (!data) return null
    return calculatePersonPerformance(data)
  }, [data])

  const radarData = useMemo<RadarChartData[]>(() => {
    if (!data) return []
    return createPersonRadarData(data)
  }, [data])

  const handleBack = () => {
    navigate('/jira')
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button 
          variant="ghost" 
          onClick={handleBack} 
          leftIcon={<ArrowLeft className="h-4 w-4" />} 
          className="mb-4"
        >
          Geri
        </Button>
        <LoadingSkeleton />
      </div>
    )
  }

  // Error state
  if (error || !data || !performanceMetrics) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button 
          variant="ghost" 
          onClick={handleBack} 
          leftIcon={<ArrowLeft className="h-4 w-4" />} 
          className="mb-4"
        >
          Geri
        </Button>
        <Card variant="default" padding="lg" className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Kullanıcı Bulunamadı
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {error || 'Bu kullanıcıya ait veri bulunamadı.'}
          </p>
          <Button onClick={refresh} leftIcon={<RefreshCw className="h-4 w-4" />}>
            Tekrar Dene
          </Button>
        </Card>
      </div>
    )
  }

  const { 
    user, 
    openTaskCount, 
    completedTaskCount, 
    tasksByStatus, 
    tasksByProject, 
    openTasks, 
    completedTasks, 
    blockedByIssues, 
    blockingIssues 
  } = data

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={handleBack} 
          leftIcon={<ArrowLeft className="h-4 w-4" />}
        >
          Genel Bakış
        </Button>
        <Button 
          variant="secondary" 
          size="sm" 
          onClick={refresh} 
          loading={isLoading} 
          leftIcon={<RefreshCw className="h-4 w-4" />}
        >
          Yenile
        </Button>
      </div>

      {/* User Header */}
      <UserHeaderCard
        user={user}
        openTaskCount={openTaskCount}
        completedTaskCount={completedTaskCount}
        metrics={performanceMetrics}
      />

      {/* Performance Score Card */}
      <PerformanceScoreCard metrics={performanceMetrics} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Tasks & Dependencies */}
        <div className="lg:col-span-2 space-y-6">
          {/* Performance Radar */}
          <JiraPerformanceRadar
            data={radarData}
            title="Performans Analizi"
            height={280}
            compact
          />

          {/* Tasks List */}
          <TasksList openTasks={openTasks} completedTasks={completedTasks} />

          {/* Dependencies */}
          {(blockedByIssues.length > 0 || blockingIssues.length > 0) && (
            <JiraDependencies 
              blockedByIssues={blockedByIssues} 
              blockingIssues={blockingIssues} 
            />
          )}
        </div>

        {/* Right Column - Stats */}
        <div className="space-y-6">
          <JiraStatusBreakdown 
            tasksByStatus={tasksByStatus} 
            title="Durum Dağılımı" 
          />
          <JiraProjectBreakdown 
            tasksByProject={tasksByProject} 
            title="Proje Dağılımı" 
          />
        </div>
      </div>

      {/* Footer */}
      <p className="text-xs text-center text-gray-400 dark:text-gray-500">
        Son güncelleme: {new Date().toLocaleString('tr-TR')}
      </p>
    </div>
  )
})

export default JiraPersonDetailEnhanced

